import { usePostHog } from 'posthog-js/react';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

import { useTrackEvent } from '@/lib/posthog/hooks/useTrackEvent';
import {
  BOOK_CALL_URL,
  openBookCallLink,
} from '@/modules/public-shortlist/constants';
import { isLocalEnv } from '@/utils/env';

interface UseShortlistTrackingProps {
  shortlistCode: string;
  enabled?: boolean;
  pageType?: 'list' | 'detail';
}

export function useShortlistTracking({
  shortlistCode,
  enabled = true,
  pageType = 'list',
}: UseShortlistTrackingProps) {
  const trackEvent = useTrackEvent();
  const posthog = usePostHog();
  const hasTrackedPageView = useRef(false);

  useEffect(() => {
    if (!enabled || !shortlistCode || hasTrackedPageView.current) {
      return;
    }

    hasTrackedPageView.current = true;

    posthog.register({
      shortlistCode,
    });

    trackEvent(
      pageType === 'detail'
        ? 'shortlistCandidateProfileViewed'
        : 'shortlistPageViewed',
      { shortlistCode },
    );

    if (!isLocalEnv) {
      posthog.startSessionRecording();
    }

    return () => {
      posthog.unregister('shortlistCode');
    };
  }, [enabled, pageType, posthog, shortlistCode, trackEvent]);

  const trackBookCallClick = () => {
    trackEvent('shortlistBookCallClicked', { shortlistCode });

    const opened = openBookCallLink();

    if (opened) {
      toast.success('Opening your booking page in a new tab…');
      return;
    }

    // The new tab was blocked (e.g. a popup blocker). Give the click a visible
    // outcome and a one-tap way to reach the booking page instead of leaving
    // the prospect wondering whether anything happened.
    toast.info('Your browser blocked the booking page from opening.', {
      duration: 10000,
      action: {
        label: 'Open it here',
        onClick: () => {
          window.location.assign(BOOK_CALL_URL);
        },
      },
    });
  };

  return { trackBookCallClick };
}
